import {Component, OnInit, signal} from '@angular/core';
import { DialogRef } from '@fundamental-ngx/core/dialog';
import {firstValueFrom} from "rxjs";
import {LocalStorageService} from "../../../../service/services/misc/local-storage/local-storage.service";
import {HttpService} from "../../../../service/services/rest/http-service/http.service";

@Component({
    selector: 'app-oidc-login-dialog',
    templateUrl: './oidc-login-dialog.component.html',
    styleUrls: ['./oidc-login-dialog.component.scss'],
    standalone: false
})
export class OidcLoginDialogComponent implements OnInit {
    customIdpPath = '';
    recentPaths: string[] = [];
    showError = false;
    serverError: string = '';
    errorText = '';
    isBusy = signal(false);
    redirectUrl: string | null = null;
    readonly PATH_PATTERN = /^\/[-\w.]+(\/[-\w.]+)*$/;
    readonly IDENTIFIER_PATTERN = /^[a-zA-Z0-9._-]{1,128}$/;
    readonly NOT_FOUND_ERROR = 'Could not find the OIDC resource. Provide the correct OIDC identifier or the path to the OIDC resource.';

    constructor(
        public dialogRef: DialogRef,
        private httpService: HttpService<any>,
        private _localStorageService: LocalStorageService
    ) {}
    ngOnInit(): void {
        const stored = this._localStorageService.get('recentCustomIdps');
        this.recentPaths = stored ? JSON.parse(stored) : [];
        this.redirectUrl = this.dialogRef.data?.redirectUrl || null;

        // Pre-fill identifier from URL params if provided
        const prefilledIdentifier = this.dialogRef.data?.identifier;
        if (prefilledIdentifier) {
            this.customIdpPath = prefilledIdentifier;
            // Trigger validation immediately to show any format errors
            this.validatePath();
        }
    }

    validatePath(): void {
        const trimmed = this.customIdpPath.trim();

        if (!trimmed) {
            this.showError = false;
            return;
        }
        this.showError = !(this.PATH_PATTERN.test(trimmed) || this.IDENTIFIER_PATTERN.test(trimmed));
    }

    isPathValid = (): boolean => {
        return this.customIdpPath.trim().length > 0 && !this.showError;
    };

    async onSubmit(): Promise<void> {
        this.errorText = '';
        if (this.showError || !this.customIdpPath?.trim()) {
            return;
        }

        const trimmed = this.customIdpPath.trim();
        const validationUrl = `/login/validate?path=${encodeURIComponent(trimmed)}`;
        this.setLoading(true);

        try {
            await firstValueFrom(this.httpService.get(validationUrl));
            this.updateRecentPaths(trimmed);
            const encodedRedirectUrl = encodeURIComponent(this.redirectUrl || '/');
            const loginUrl = `/login?redirecturl=${encodedRedirectUrl}&idp=custom.idp&oidcPath=${encodeURIComponent(trimmed)}`;
            this.redirectToLogin(loginUrl);
        } catch (err: any) {
            this.serverError = err?.error?.error || this.NOT_FOUND_ERROR;
            this.errorText = this.NOT_FOUND_ERROR;
        } finally {
            this.setLoading(false);
        }
    }

    setLoading(isLoading: boolean) {
        this.isBusy.set(isLoading);
        this.dialogRef.loading(isLoading);
    }

    redirectToLogin(url: string): void {
        window.location.href = url;
    }

    updateRecentPaths(path: string): void {
        if (!this.recentPaths.includes(path)) {
            this.recentPaths.unshift(path);
            this.recentPaths = this.recentPaths.slice(0, 5);
            this._localStorageService.set('recentCustomIdps', JSON.stringify(this.recentPaths));
        }
    }

    onClose(): void {
        this.dialogRef.close();
    }
}
